import { useCouponStore } from '@/store/coupon';
import { useTaskStore } from '@/store/task';
import { elk } from '@tencent/ppd-base-libs';
import { getPromiseHandler } from '@tencent/wphk-base-utils';
import { reactive } from '@vue/composition-api';
import dayjs from 'dayjs';
import { CouponState, ExActAndConfigCouponInfo } from '@/types/coupon';
import { getServerTime } from '../utils/date-helper';
import { getStorageMarks, updateStorageMarks } from '../utils/storage-helper';
import { queryInfoFromDp, formatBannerData, FormatBannerDataRes } from '@/business/dp-query';
import { TaskStatus } from '@/types/task';

/** 正式-投放页面id */
const DP_PAGE = '110004000035';
/** 正式-投放位置id */
const DP_POSITION = '1100040000350002';
// /** 开发-投放页面id */
// const DP_PAGE = '110004599066';
// /** 开发-投放位置id */
// const DP_POSITION = '1100045990660002';

/** 推荐的额外信息 */
type ExtraInfo = {
  /** 推荐内容类型为跳转H5 - 跳转url */
  jumpUrl?: string;

  /** 推荐内容类型为拉起小程序 - appId */
  miniAppId?: string;
  /** 推荐内容类型为拉起小程序 - username */
  miniAppUsername?: string;
  /** 推荐内容类型为拉起小程序 - 小程序指定页面 */
  miniAppPath?: string;

  /** 推荐内容类型为优惠券 - 优惠券Id */
  couponId?: string;

  /** 推荐内容类型为任务 - 任务Id */
  taskId?: string;
};

/** 推荐信息 */
export type RecommendInfo = {
  /** 内容类型：券/任务/H5/小程序 */
  contentType?: RecommendContentType;
  /** 推荐内容图片 */
  contentImg?: string;
  /** 上报扩展字段ftag */
  biTag?: string;
  /** 推荐券信息 */
  extraInfo?: ExtraInfo;
};
/** 内容类型 */
export enum RecommendContentType {
  /** 优惠券 */
  coupon = 'coupon',
  /** 任务 */
  task = 'task',
  /** 跳转H5 */
  h5 = 'h5',
  /** 拉起小程序 */
  miniapp = 'miniapp',
};

const showData = reactive({
  /** 是否展示推荐弹窗 */
  isNeedShow: false,  // isNeedShow变更不会触发展示弹框
  /** 是否展示推荐弹窗 */
  isShow: false,  // isShow变更会触发展示弹框，在hooks/use-dialog-manger中由isNeedShow控制
  /** 弹窗判断逻辑及数据是否ready */
  isReady: false, // 注：isReady为true表示数据ready了，但不一定展示弹框，展示弹框由hooks/use-dialog-manger控制
});


const recommendInfoData: RecommendInfo = reactive({
  /** 内容类型：券/任务/H5/小程序 */
  contentType: undefined,
  /** 推荐内容图片 */
  contentImg: '',
  /** 上报扩展字段 */
  biTag: '',
  /** 推荐信息 */
  extraInfo: undefined,
});

/** 弹窗句柄 */
const promiseHandler = getPromiseHandler();

export const useRecommend = () => {
  const couponStore = useCouponStore();

  const setRecommendInfo = (targetRecommendInfo: RecommendInfo) => {
    Object.assign(recommendInfoData, targetRecommendInfo);
    console.log('推荐弹窗：setRecommendInfo', recommendInfoData);
  };

  const showDialog = async () => {
    showData.isShow = true;
    // mark 弹窗
    markRecommendDialogView();
    return promiseHandler.promise;
  };

  const hideDialog = () => {
    showData.isShow = false;
    promiseHandler.resolve(true);
  };

  /** 校验：是否有新手任务（弹框），今天未展示过 且 本月小于最大展示次数 */
  const preCheck = (): boolean => {
    /** 频率限制：一天一次，一个月至多三次 */
    const shouldShowWithFrequencyCtl = shouldShowRecommendDialog();

    console.log(`推荐弹窗：频率限制：shouldShowWithFrequencyCtl:${shouldShowWithFrequencyCtl}`);
    elk({
      key: 'eStampRecommendDialog',
      str1: 'preCheck',
      msg: `houldShowWithFrequencyCtl:${shouldShowWithFrequencyCtl}`,
    });
    return shouldShowWithFrequencyCtl;
  };
  /** 获取推荐数据，undeined则是没找到有效数据 */
  const getRecommendInfoFormDpData = (dpData: FormatBannerDataRes): {
    contentType: RecommendContentType;
    extraInfo: ExtraInfo;
  } | undefined => {
    // 1、配置的是跳转H5
    if (dpData.bannerJumpInfo.jumpUrl) {
      return {
        contentType: RecommendContentType.h5,
        extraInfo: {
          jumpUrl: dpData.bannerJumpInfo.jumpUrl,
        },
      };
    }
    // 2、配置的是拉起小程序
    if (dpData.bannerJumpInfo.miniAppUsername) {
      return {
        contentType: RecommendContentType.miniapp,
        extraInfo: {
          miniAppId: dpData.bannerJumpInfo.miniAppId,
          miniAppUsername: dpData.bannerJumpInfo.miniAppUsername,
          miniAppPath: dpData.bannerJumpInfo.miniAppPath,
        },
      };
    }
    // 3、配置的是优惠券
    if (dpData.extraData.couponId) {
      // 需要判断couponId对应的券是否可用
      if (!isCouponIdValid(couponStore.formattedCouponList, dpData.extraData.couponId)) {
        console.log(`推荐弹窗：couponId(${dpData.extraData.couponId})状态不可用，券不可用`);
        return;
      }
      return {
        contentType: RecommendContentType.coupon,
        extraInfo: {
          couponId: dpData.extraData.couponId,
        },
      };
    }
    // 4、配置的是任务
    if (dpData.extraData.taskId) {
      // 需要判断taskId对应的任务是否可用
      if (!isTaskIdValid(dpData.extraData.taskId)) {
        console.log(`推荐弹窗：taskId(${dpData.extraData.taskId})状态不可用，任务不可用`);
        return;
      }
      return {
        contentType: RecommendContentType.task,
        extraInfo: {
          taskId: dpData.extraData.taskId,
        },
      };
    }
    // 5、纯展示
    return {
      contentType: RecommendContentType.h5,
      extraInfo: {},
    };
  };

  /** 推荐弹窗资格校验&初始化 */
  const checkAndInit = async () => {
    // 1、新手任务，今天是否展示过
    if (!preCheck()) return;

    // 2、投放数据查询/校验
    const dpList = await queryInfoFromDp(DP_PAGE, DP_POSITION, formatBannerData);
    console.log('推荐弹窗: 投放计划列表', dpList);
    // 2.1、是否有投放计划
    if (!dpList.length) {
      // 没有可用的推荐投放计划 或 没有配置bannerImgUrl
      console.log('推荐弹窗: 没有投放计划');
      return;
    }

    // 2.2 获取第1条有效的投放计划
    const len = dpList.length;

    let recommendInfo: RecommendInfo | undefined = undefined;
    console.log('推荐弹窗， couponStore.formattedCouponList: ', couponStore.formattedCouponList.map(item => [item.couponId, item.couponState]));
    const { taskInfoList } = useTaskStore();
    console.log('推荐弹窗，taskStore.taskInfoList: ', taskInfoList);
    for (let i = 0; i < len; i++) {
      const dpData = dpList[i];
      if (!dpData.bannerImgUrl) continue; // 配置内容有误，属于无效投放计划
      // 2.2.1 获取 优惠券/任务是否满足条件 ｜ 是H5 ｜ 是小程序 推荐额外信息
      const info = getRecommendInfoFormDpData(dpData);
      if (info) {
        // 找到第1条有效投放数据，则结束
        const { bannerImgUrl: contentImg, bannerTag: biTag } = dpData;
        const { contentType, extraInfo } = info;
        recommendInfo = {
          contentImg,
          biTag,
          contentType,
          extraInfo,
        };
        break;
      }
    }
    if (!recommendInfo) return;

    // 设置推荐内容
    setRecommendInfo(recommendInfo);

    showData.isNeedShow = true;
  };

  const init = async () => {
    await checkAndInit();
    showData.isReady = true;
  };

  return {
    /** 初始化推荐模块 */
    init,
    /** 弹窗展示数据 */
    showData,
    /** 推荐数据 */
    recommendInfoData,
    /** 展示弹窗 */
    showDialog,
    /** 关闭弹窗 */
    hideDialog,
  };
};

/** 判断couponId状态是否有效 */
const isCouponIdValid = (couponList: ExActAndConfigCouponInfo[], couponId: string): boolean => {
  const targetList = couponList.filter((ele) => ele.couponId === couponId && isRecommendCouponInStock(ele));

  return targetList.length > 0;
};

/** 判断taskId状态是否有效 */
const isTaskIdValid = (taskId: string): boolean => {
  const { getTaskInfo, taskInfoList } = useTaskStore();
  // 用户纬度：任务Id存在，并且已完成次数 < 最大完成次数
  const findItem = taskInfoList.find((ele) => {
    const { taskNameId, finishCount, maxTimesPerStage } = ele;
    return taskNameId === taskId && finishCount < maxTimesPerStage;
  });
  if (!findItem) return false;

  // 任务纬度：先判断印花是否正常
  const taskInfo = getTaskInfo(taskId);
  console.log(`推荐弹窗：taskInfo(${taskId})`, taskInfo);
  return taskInfo.status === TaskStatus.inProgress;
};

/** 每月展示最大次数 */
const MAX_VIEW_TIMES = 3;
const getStorageKey = () => `E-STAMP-RECOMMEND-DIALOG-${dayjs(getServerTime()).format('YYYYMM')}`;

/** 是否能展示推荐弹窗：今天未展示过 且 小于最大展示次数 */
const shouldShowRecommendDialog = () => {
  // 条件：今天未展示过 且 小于最大展示次数
  const marks = getStorageMarks(getStorageKey());
  const curTime = dayjs(getServerTime()).format('YYYYMMDD');
  return !marks.includes(curTime) && marks.length < MAX_VIEW_TIMES;
};

/** 标记推荐弹窗弹出动作 */
const markRecommendDialogView = () => {
  const marks = getStorageMarks(getStorageKey());
  const curTime = dayjs(getServerTime()).format('YYYYMMDD');
  if (!marks.includes(curTime)) {
    updateStorageMarks(getStorageKey(), [...marks, curTime]);
  }
};

/** 优惠券是否有库存 */
const isRecommendCouponInStock =
  (couponInfo: ExActAndConfigCouponInfo) => [CouponState.enable, CouponState.unable].includes(couponInfo.couponState);
